import PropTypes from 'prop-types'
import { useLocation, useNavigate } from 'react-router-dom'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { ToggleSwitch } from '@/components/ui/toggle-switch'
import { useDesignVersionToggle } from '@/hooks'

import { selectLoyaltySettings } from '../../../reducers/loyaltyReducer'
import { selectActiveBuilding } from '../../../reducers/buildingSelectorReducer'
import { selectUserRole } from '../../../reducers/userDataReducer'
import Icon from '../../Icons'

import ToolbarMenu from './components/ToolbarMenu/ToolbarMenu'

const GIFT_CARDS_PATH = '/dashboard/building-dashboard/rewards/gift-cards'

const RewardsToolbar = ({
  userRole,
  selectedBuilding,
  setShowAddFunds,
  setShowLoyaltySettings,
  settings,
  buildingData,
}) => {
  const { t } = useTranslation(['rewards', 'gift-cards'])
  const navigate = useNavigate()
  const location = useLocation()
  const { loyaltyAvailable, rewardsAvailable } = buildingData || {}

  const isGiftCardsLegacy = location.pathname.includes(`${GIFT_CARDS_PATH}/legacy`)
  const isGiftCardsRoute = location.pathname.includes(GIFT_CARDS_PATH)
  const giftCardsBuildingId = selectedBuilding ? String(selectedBuilding.id) : ''

  const { toggle: handleGiftCardsToggle } = useDesignVersionToggle({
    group: 'gift-cards',
    screen: 'GiftCards',
    to: {
      params: { buildingId: giftCardsBuildingId },
      path: isGiftCardsLegacy
        ? '/dashboard/building-dashboard/rewards/gift-cards/:buildingId'
        : '/dashboard/building-dashboard/rewards/gift-cards/legacy/:buildingId',
    },
    version: isGiftCardsLegacy ? 'legacy' : 'new',
  })

  const routes = [
    {
      path: `/dashboard/building-dashboard/rewards/dashboard${
        selectedBuilding ? `/${selectedBuilding.id}` : ''
      }`,
      name: t('rewards:toolbar.dashboardTab'),
    },
    {
      // Note: must match the currently-active design's own path (new vs legacy) - the tab's
      // NavLink only highlights when the current URL starts with `to`, and `/legacy` isn't a
      // prefix of the plain gift-cards path (or vice versa).
      path: `/dashboard/building-dashboard/rewards/gift-cards${isGiftCardsLegacy ? '/legacy' : ''}${
        selectedBuilding ? `/${selectedBuilding.id}` : ''
      }`,
      name: t('rewards:toolbar.giftCardsTab'),
      lock: !rewardsAvailable && userRole !== 'Administrator',
    },
    {
      path: `/dashboard/building-dashboard/rewards/loyalty${
        selectedBuilding ? `/${selectedBuilding.id}` : ''
      }`,
      name: t('rewards:toolbar.loyaltyTab'),
      lock: !loyaltyAvailable && userRole !== 'Administrator',
    },
  ]

  return (
    <div className="rewards-toolbar">
      <div className="rewards-toolbar__wrapper">
        <span className="rewards-toolbar__title">{t('rewards:toolbar.title')}</span>
        <div className="rewards-toolbar__divider" />
        <span className="rewards-toolbar__text">{t('rewards:toolbar.totalBalanceLabel')}</span>
        <div className="button-value-display">
          <span>${selectedBuilding ? selectedBuilding.rewardsBalance : 0}</span>
          <button
            className="button button-black"
            onClick={() => setShowAddFunds(true)}
            disabled={!selectedBuilding}
          >
            {t('rewards:toolbar.addFundsButton')}
          </button>
        </div>
        {loyaltyAvailable && (
          <div className={`button-value-display`}>
            <div>
              <span>{t('rewards:toolbar.unredeemedPointsValueLabel')}</span>
              <div>
                <span>{`$${settings ? settings.health.unredeemedPointsValue : 0}`}</span>
                <span className={`${settings ? settings.health.healthLevel : ''}`}>
                  {settings ? settings.health.healthLevel : ''}
                </span>
              </div>
            </div>
            <button
              className="button button-black"
              onClick={() => setShowLoyaltySettings(true)}
              disabled={!selectedBuilding || !settings}
            >
              <Icon icon="settings" viewBox="0 0 24 24" />
            </button>
          </div>
        )}
        <div className="rewards-toolbar__actions">
          {isGiftCardsRoute && (
            <ToggleSwitch
              falsyLabel={t('gift-cards:designToggle.old')}
              truthyLabel={t('gift-cards:designToggle.new')}
              checked={!isGiftCardsLegacy}
              onChange={handleGiftCardsToggle}
            />
          )}
          <button
            className="button button-black-outline"
            onClick={() => navigate(`/dashboard/building-dashboard/financials`)}
          >
            {t('rewards:toolbar.financialsButton')}
          </button>
          {loyaltyAvailable && (
            <button
              className="button button-black-outline"
              onClick={() =>
                navigate(`/dashboard/building-dashboard/rewards/settings/${selectedBuilding.id}`)
              }
            >
              <Icon icon="settings" viewBox="0 0 24 24" />
              {t('rewards:toolbar.settingsButton')}
            </button>
          )}
        </div>
      </div>

      <ToolbarMenu routes={routes} />
    </div>
  )
}

RewardsToolbar.propTypes = {
  userRole: PropTypes.string,
  selectedBuilding: PropTypes.object,
  settings: PropTypes.object,
  buildingData: PropTypes.object,
  setShowAddFunds: PropTypes.func,
  setShowLoyaltySettings: PropTypes.func,
}

export default connect((state) => ({
  userRole: selectUserRole(state),
  settings: selectLoyaltySettings(state),
  buildingData: selectActiveBuilding(state),
}))(RewardsToolbar)
